import { jsx as _jsx } from "react/jsx-runtime";
import { AutocompleteWrapper } from '@src/components/AutocompleteWrapper';
import { ProgramContext } from '@src/contexts/ProgramContextProvider';
import { sanitizePaymentRequestForSpec } from '@src/utilities/paymentRequestHelper';
import { debounce } from 'lodash';
import { MuiSingleSelect, useLocale, useLookupOptions } from 'pe-core-frontend';
import { useCallback, useContext, useEffect, useState } from 'react';
export const FieldOptionsLookup = ({ clientId, entityType, fieldCode, label, value, errors, onChange, showAllAtOnce, additionalInfo, helpText, placeholder, required, showSearchIcon, pageSize = 500, }) => {
    const { locale } = useLocale();
    const [options, setOptions] = useState([]);
    const { benefitProgramProps, paymentRequest, program } = useContext(ProgramContext);
    const [totalPages, setTotalPages] = useState(1);
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(false);
    const [paginationLoading, setPaginationLoading] = useState(false);
    const [query, setQuery] = useState('');
    const [hasMorePages, setHasMorePages] = useState(true);
    const { fetchOptions } = useLookupOptions({
        extraData: {
            program: benefitProgramProps,
            paymentRequest: paymentRequest
                ? sanitizePaymentRequestForSpec(paymentRequest)
                : undefined,
        },
    });
    const [selectedOption, setSelectedOption] = useState(null);
    // Reset pagination when query changes
    useEffect(() => {
        setPage(1);
        setHasMorePages(true);
    }, [query, program]);
    useEffect(() => {
        (async () => {
            if (showAllAtOnce && hasMorePages) {
                setLoading(true);
                try {
                    const result = await fetchOptions({
                        clientId,
                        entityType,
                        fieldCode,
                        query: showAllAtOnce ? query : value,
                        fetchAll: false, // Set to false to get paginated results
                        pagination: { page, pageSize },
                    });
                    const newOptions = result.options || [];
                    const total = result.total || 0;
                    // Calculate if there are more pages
                    const calculatedTotalPages = Math.ceil(total / pageSize);
                    setTotalPages(calculatedTotalPages);
                    setHasMorePages(page < calculatedTotalPages);
                    // APPEND new options to existing ones instead of replacing
                    // First page - replace all options
                    // Subsequent pages - append to existing options and remove any duplicates
                    if (page === 1) {
                        setOptions(newOptions);
                    }
                    else {
                        setOptions(prevOptions => {
                            const existingValues = new Set(prevOptions.map(opt => opt.value));
                            const uniqueNewOptions = newOptions.filter(opt => !existingValues.has(opt.value));
                            return [...prevOptions, ...uniqueNewOptions];
                        });
                    }
                }
                catch (error) {
                    console.error('Failed to fetch options:', error);
                }
                finally {
                    setLoading(false);
                    setPaginationLoading(false);
                }
            }
        })();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showAllAtOnce, hasMorePages, locale, benefitProgramProps, page, query, program]);
    useEffect(() => {
        if (options && options.length > 0) {
            const opt = options.find(opt => opt.value === value);
            // Keep the current selection if a refetch reset to page 1 and the
            // selected option (e.g. "Other" on the last page) isn't on it
            setSelectedOption(prev => opt ?? (prev?.value === value ? prev : null));
        }
    }, [options, value]);
    const handleChange = (e) => {
        onChange(e.selection);
        setSelectedOption(e.selection);
        // reset the query and pagination when the selection is cleared
        if (!e.selection) {
            setQuery('');
            setPage(1);
            setHasMorePages(true);
        }
    };
    const handleLoad = async (query, callback) => {
        if (!options)
            return [];
        try {
            const { options } = await fetchOptions({ clientId, entityType, fieldCode, query });
            setOptions(options);
            callback(options);
        }
        catch (error) {
            console.error('Failed to load field lookup options:', error);
        }
    };
    const throttleSetSearchText = useCallback(debounce((value) => setQuery(value), 300), []);
    const handleSearch = (value) => {
        throttleSetSearchText(value); // This will trigger the useLookupOptions hook - after 300ms of no input
    };
    const handleScrollEnd = () => {
        if (!loading && hasMorePages && page < totalPages) {
            setPaginationLoading(true);
            setPage(prevPage => prevPage + 1);
        }
    };
    // Don't translate program levels, school names, field of study dropdowns & fee type dropdowns
    // program level names, field of study names & fee type names translations come from content_items
    // school names are as is - they're Proper Nouns
    const shouldNotTranslate = fieldCode === 'program_level_id' ||
        fieldCode === 'field_of_study_id' ||
        fieldCode === 'school_id' ||
        fieldCode === 'fee_type_id';
    return showAllAtOnce ? (_jsx(MuiSingleSelect, { label: label, options: options, value: selectedOption?.value || '', onChange: handleChange, errors: errors, additionalInfo: additionalInfo, helpText: helpText, placeholder: placeholder, required: required, showSearchIcon: showSearchIcon, onScrollEnd: handleScrollEnd, onSearch: handleSearch, loading: loading, paginationLoading: paginationLoading, disableWeglot: shouldNotTranslate })) : (_jsx(AutocompleteWrapper, { label: label, loadOptions: handleLoad, defaultOptions: selectedOption ? [selectedOption] : [], value: selectedOption, onChange: handleChange, cacheOptions: false, errors: errors, additionalInfo: additionalInfo, helpText: helpText, placeholder: placeholder, required: required, disableWeglot: shouldNotTranslate }));
};
